import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Button } from 'ui-components/components/button';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { isEmailAddressValid } from '../../services/email-sanitation/isEmailAddressValid';
import { sanitizeEmailAddress } from '../../services/email-sanitation/sanitizeEmailAddress';
import style from './style.module.less';
import { LabelPurchaseButton } from '../label-purchase-button';
import { EventSourceStrings } from '../../tracking/tracking-constants';

const messages = defineMessages({
  header: {
    description: 'returnLabelDispatcher.header',
    defaultMessage: 'Return Labels',
    id: 'MTEVaa',
  },
  emailOption: {
    description: 'returnLabelDispatcher.emailLabelOption',
    defaultMessage: 'Email label',
    id: 'FWDTS6',
  },
  downloadOption: {
    description: 'returnLabelDispatcher.downloadLabelOption',
    defaultMessage: 'Download label now',
    id: '2K09qN',
  },
  emailAddressListLabel: {
    description: 'returnLabelDispatcher.emailAddressListLabel',
    defaultMessage: 'Email to',
    id: 'LouTfi',
  },
  emailAddressListInstructions: {
    description: 'returnLabelDispatcher.emailAddressListInstructions',
    defaultMessage: 'Use commas for multiple addresses',
    id: 'iGI+Lw',
  },
  emailAction: {
    description: 'returnLabelDispatcher.emailAction',
    defaultMessage: 'Authorize & Email',
    id: 'Cf+1PD',
  },
  downloadAction: {
    description: 'returnLabelDispatcher.downloadAction',
    defaultMessage: 'Download PDF',
    id: 'YRImlC',
  },
});

const EMAIL_OPTION = 'EMAIL_OPTION';
const DOWNLOAD_OPTION = 'DOWNLOAD_OPTION';

const buildEmailAddressCsv = (emailAddresses = []) => {
  return emailAddresses.join(',');
};

export default class ReturnLabelDispatcher extends React.PureComponent {
  static propTypes = {
    emailAddresses: PropTypes.arrayOf(PropTypes.string),
    onEmailButtonClicked: PropTypes.func.isRequired,
    onDownloadButtonClicked: PropTypes.func.isRequired,
    fulfillmentPlanId: PropTypes.string.isRequired,
  };

  static defaultProps = {
    emailAddresses: [],
  };

  static getDerivedStateFromProps(props, state) {
    const shouldRebuildEmailAddressCsvFromProps = state.previousEmailAddressesFromProps !== props.emailAddresses;
    const emailAddressCsv = shouldRebuildEmailAddressCsvFromProps ? buildEmailAddressCsv(props.emailAddresses) : state.emailAddressCsv;
    const previousEmailAddressesFromProps = shouldRebuildEmailAddressCsvFromProps
      ? props.emailAddresses
      : state.previousEmailAddressesFromProps;

    return {
      ...state,
      emailAddressCsv,
      previousEmailAddressesFromProps,
    };
  }

  constructor(props) {
    super(props);
    this.state = {
      selectedOption: EMAIL_OPTION,
      emailAddressCsv: buildEmailAddressCsv(this.props.emailAddresses),
      doesEmailAddressCsvHaveError: false,
    };
  }

  onEmailAddressCsvChanged = (emailAddressCsv) => {
    this.setEmailAddressCsv(emailAddressCsv);
    this.setDoesEmailAddressCsvHaveError(false);
  };

  onEmailOptionSelected = () => {
    this.setSelectedOption(EMAIL_OPTION);
  };

  onEmail = () => {
    const emailAddresses = this.getSanitizedEmailAddresses();
    if (emailAddresses.length === 0 || !this.areAllEmailAddressesValid(emailAddresses)) {
      this.setDoesEmailAddressCsvHaveError(true);
      return;
    }
    this.setDoesEmailAddressCsvHaveError(false);
    this.props.onEmailButtonClicked(this.props.fulfillmentPlanId, true, this.getSanitizedEmailAddresses());
  };

  onDownloadOptionSelected = () => {
    this.setDoesEmailAddressCsvHaveError(false);
    this.setSelectedOption(DOWNLOAD_OPTION);
  };

  onDownload = () => {
    this.props.onDownloadButtonClicked(this.props.fulfillmentPlanId, false);
  };

  setSelectedOption = (selectedOption) => {
    this.setState({
      selectedOption,
    });
  };

  setEmailAddressCsv = (emailAddressCsv) => {
    this.setState({
      emailAddressCsv,
    });
  };

  setDoesEmailAddressCsvHaveError = (hasError) => {
    this.setState({
      doesEmailAddressCsvHaveError: hasError,
    });
  };

  getUnsanitizedEmailAddresses = () => {
    const { emailAddressCsv } = this.state;
    if (emailAddressCsv.length > 0) {
      return emailAddressCsv.split(',');
    }
    return [];
  };

  getSanitizedEmailAddresses = () => {
    return this.getUnsanitizedEmailAddresses().map((emailAddress) => sanitizeEmailAddress(emailAddress));
  };

  getSelectedOptionMetadata = () => {
    return this.optionsMetadata[this.state.selectedOption];
  };

  areAllEmailAddressesValid = (emailAddresses) => {
    const coalescedEmailAddresses = emailAddresses || this.getSanitizedEmailAddresses();
    if (coalescedEmailAddresses.length === 0) {
      return true;
    }
    return coalescedEmailAddresses.every((emailAddress) => isEmailAddressValid(emailAddress));
  };

  optionsMetadata = {
    [EMAIL_OPTION]: {
      optionMessage: messages.emailOption,
      actionMessage: messages.emailAction,
      onSelected: this.onEmailOptionSelected,
      onAction: this.onEmail,
    },
    [DOWNLOAD_OPTION]: {
      optionMessage: messages.downloadOption,
      actionMessage: messages.downloadAction,
      onSelected: this.onDownloadOptionSelected,
      onAction: this.onDownload,
    },
  };

  renderOptions = () => {
    return (
      <RadioCheckList className={style['option-wrapper']}>
        {Object.keys(this.optionsMetadata).map((optionMetadataKey) => {
          const optionMetadata = this.optionsMetadata[optionMetadataKey];
          return (
            <RadioCheckListItem
              key={optionMetadataKey}
              className={style.option}
              isChecked={this.state.selectedOption === optionMetadataKey}
              onChecked={optionMetadata.onSelected}
            >
              <FormattedMessage {...optionMetadata.optionMessage} />
            </RadioCheckListItem>
          );
        })}
      </RadioCheckList>
    );
  };

  renderForm = () => {
    const optionMetadata = this.getSelectedOptionMetadata();
    return (
      <>
        <FloatTextArea
          className={style['email-address-csv-textarea']}
          disabled={this.state.selectedOption !== EMAIL_OPTION}
          hasError={this.state.doesEmailAddressCsvHaveError}
          label={<FormattedMessage {...messages.emailAddressListLabel} />}
          onChange={this.onEmailAddressCsvChanged}
          value={this.state.selectedOption === EMAIL_OPTION ? this.state.emailAddressCsv : ''}
        />
        <div className={style['email-option-footer']}>
          <div className={style['email-address-list-instructions']}>
            <FormattedMessage {...messages.emailAddressListInstructions} />
          </div>
          <LabelPurchaseButton labelPurchaseCallback={optionMetadata.onAction} trackingId={EventSourceStrings.OLD_RETURN_LABEL_BUTTON}>
            {({ onClick }) => (
              <Button onClick={onClick} theme='primary'>
                <FormattedMessage {...optionMetadata.actionMessage} />
              </Button>
            )}
          </LabelPurchaseButton>
        </div>
      </>
    );
  };

  render() {
    return (
      <div className='return-label-dispatcher'>
        {this.renderOptions()}
        {this.renderForm()}
      </div>
    );
  }
}
